import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, Lightbulb, Handshake, TrendingUp } from "lucide-react";
import { useCmsText } from "@/hooks/useCmsText";
import globeVideo from "@/assets/globe-rotate.mp4";

const serviceIcons = [
  { 
    icon: BarChart3, 
    labelKey: "hero.services.marketAnalysis",
    defaultLabel: "Marktanalyse"
  },
  { 
    icon: Lightbulb, 
    labelKey: "hero.services.strategyDevelopment",
    defaultLabel: "Strategieentwicklung"
  },
  { 
    icon: Handshake, 
    labelKey: "hero.services.partnerSourcing",
    defaultLabel: "Partnersuche"
  },
  { 
    icon: TrendingUp, 
    labelKey: "hero.services.growthSupport",
    defaultLabel: "Wachstumsbegleitung"
  },
];

export function HeroSection() {
  const { t } = useTranslation();
  const cms = useCmsText("landing");
  const [showVideo, setShowVideo] = useState(false);

  // Defer the background video until after first paint so it never
  // competes with the LCP image for bandwidth.
  useEffect(() => {
    const start = () => setShowVideo(true);
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const id = idle ? idle(start, { timeout: 2000 }) : window.setTimeout(start, 1200);
    return () => {
      const cancelIdle = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (idle && cancelIdle) cancelIdle(id as number);
      else window.clearTimeout(id as number);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden w-full">
      {/* Background Image - World Map */}
      <div className="absolute inset-0 overflow-hidden bg-black">
        {showVideo && (
          <video
            src={globeVideo}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-[hsl(185,70%,8%)]/40" />
      </div>
      
      {/* Main Content - Centered */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10 px-4 sm:px-6 lg:px-8 w-full pb-20 md:pb-28">
        <div className="flex flex-col items-center text-center">
          {/* Logo with glassmorphism panel */}
          <img 
            src="/hero-logo.webp" 
            alt="M&H Global Market" 
            width={900}
            height={675}
            loading="eager"
            {...{ fetchpriority: "high" }}
            decoding="sync"
            className="h-64 md:h-[22rem] lg:h-[28rem] w-auto mb-2 md:mb-4 brightness-0 invert drop-shadow-[0_0_12px_rgba(255,255,255,0.4)]"
          />


          {/* Tagline - centered */}
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display text-white mb-4 md:mb-6 leading-[1.15] tracking-tight">
            {cms("hero.tagline", t("hero.tagline"))}
          </h1>

          {/* Service Icons - Compact Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 w-fit mx-auto">
            {serviceIcons.map((service) => (
              <div
                key={service.labelKey}
                className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 hover:border-white/30 hover:scale-[1.02] transition-all duration-200 cursor-pointer group"
                onClick={() => document.getElementById('leistungen')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <service.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white shrink-0" strokeWidth={1.5} />
                <span className="text-xs sm:text-sm text-white/90 font-medium whitespace-nowrap">
                  {t(service.labelKey, service.defaultLabel).replace('\n', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 bg-[hsl(40,25%,96%)] py-6 sm:py-8 md:py-10 w-full">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[hsl(185,40%,30%)] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl">
            {cms("hero.subdescription", t("hero.whyChooseUsDesc", "Wir bieten maßgeschneiderte Lösungen und strategische Beratung, um Ihrem Unternehmen zum Erfolg auf internationalen Märkten zu verhelfen."))}
          </p>
        </div>
      </div>

    </section>
  );
}
